import { SIGN_FILE_URLS } from "../services/generic/GenericServices";
import { handleError } from "./AppFunctions";
import {
  batchQueue,
  ICacheEntry,
  pendingRequests,
  urlCache,
} from "./SignedFileUrlCache";

interface ISignedFileUrl {
  path: string;
  url: string | null;
  expiresInSeconds: number;
}

const SUCCESS_CODE = 200;

const CACHE_LIFETIME_RATIO = 0.75;

const FALLBACK_TTL_SECONDS = 30;

const MAX_PATHS_PER_REQUEST = 100;

const DOWNLOAD_PREFIX = "download:";

const toCacheKey = (path: string, download: boolean): string =>
  download ? `${DOWNLOAD_PREFIX}${path}` : path;

const readCache = (path: string): ICacheEntry | undefined => {
  const entry = urlCache.get(path);
  if (!entry) return undefined;
  if (entry.expiresAt <= Date.now()) {
    urlCache.delete(path);
    return undefined;
  }
  return entry;
};

const requestBatch = async (
  paths: string[],
  download: boolean
): Promise<void> => {
  const response = await SIGN_FILE_URLS(paths, download);
  const signed: ISignedFileUrl[] =
    response?.code === SUCCESS_CODE ? response.data ?? [] : [];

  for (const path of paths) {
    const match = signed.find((entry) => entry.path === path);
    const lifetime = match?.expiresInSeconds || FALLBACK_TTL_SECONDS;
    urlCache.set(toCacheKey(path, download), {
      url: match?.url ?? null,
      expiresAt: Date.now() + lifetime * CACHE_LIFETIME_RATIO * 1000,
    });
  }
};

const flushQueue = async (): Promise<Map<string, string | null>> => {
  const paths = batchQueue.paths;
  batchQueue.paths = [];
  batchQueue.timer = null;
  batchQueue.batch = null;
  const resolve = batchQueue.resolve;
  batchQueue.resolve = null;

  try {
    for (let i = 0; i < paths.length; i += MAX_PATHS_PER_REQUEST) {
      await requestBatch(paths.slice(i, i + MAX_PATHS_PER_REQUEST), false);
    }
  } catch (error) {
handleError(error , 'flushQueue')  }

  const urls = new Map<string, string | null>();
  paths.forEach((path) => urls.set(path, urlCache.get(path)?.url ?? null));
  resolve?.(urls);
  return urls;
};

const enqueue = (path: string): Promise<string | null> => {
  if (!batchQueue.paths.includes(path)) batchQueue.paths.push(path);

  if (!batchQueue.batch) {
    batchQueue.batch = new Promise((resolve) => {
      batchQueue.resolve = resolve;
      batchQueue.timer = setTimeout(flushQueue, 0);
    });
  }

  return batchQueue.batch.then((urls) => urls.get(path) ?? null);
};

export const resolveFileUrl = async (
  path: string | undefined
): Promise<string | null> => {
  const key = path?.trim();
  if (!key) return null;

  const cached = readCache(key);
  if (cached) return cached.url;

  const inFlight = pendingRequests.get(key);
  if (inFlight) return inFlight;

  const request = enqueue(key).finally(() => pendingRequests.delete(key));
  pendingRequests.set(key, request);
  return request;
};

export const resolveDownloadUrl = async (
  path: string | undefined
): Promise<string | null> => {
  const key = path?.trim();
  if (!key) return null;

  const cached = readCache(toCacheKey(key, true));
  if (cached) return cached.url;

  try {
    await requestBatch([key], true);
  } catch (error) {
handleError(error, 'resolveDownloadUrl')    
return null;
  }
  return urlCache.get(toCacheKey(key, true))?.url ?? null;
};

export const invalidateFileUrl = (path: string | undefined): void => {
  const key = path?.trim();
  if (!key) return;
  urlCache.delete(key);
  urlCache.delete(toCacheKey(key, true));
};

export { clearFileUrlCache } from "./SignedFileUrlCache";
